import { memo } from 'react'
import { useNavigate } from 'react-router-dom'
import { PencilIcon } from '@heroicons/react/24/outline'

/**
 * AnnouncementTableRow - Single announcement row in admin table
 * 
 * @atomic - Single row display logic
 * @idempotent - Same props produce same output
 * 
 * @param {Object} announcement - Announcement data
 * @param {boolean} isSelected - Whether row is selected
 * @param {Function} onSelect - Selection toggle handler
 * @param {Function} onTogglePublish - Publish/unpublish handler
 * @param {Function} onTogglePin - Pin/unpin handler
 * @param {Function} onDelete - Delete handler
 * @param {Function} getTypeIcon - Get icon component for announcement type
 * @param {Function} getTypeColor - Get color classes for announcement type
 */
const AnnouncementTableRow = memo(function AnnouncementTableRow({
  announcement,
  isSelected,
  onSelect,
  onTogglePublish,
  onTogglePin,
  onDelete,
  getTypeIcon,
  getTypeColor
}) {
  const navigate = useNavigate()
  const TypeIcon = getTypeIcon(announcement.announcementType)
  const typeColor = getTypeColor(announcement.announcementType)

  return (
    <tr className={isSelected ? 'bg-blue-50' : ''}>
      <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onSelect(announcement.id)}
          className="rounded border-gray-300 text-primary focus:ring-primary"
        />
      </td>
      <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="flex items-center gap-2 mr-3">
            <div className={`h-8 w-8 rounded-lg border flex items-center justify-center ${typeColor}`}>
              <TypeIcon className="h-4 w-4" />
            </div>
            {announcement.authorAvatar && (
              <a 
                href="https://instagram.com/alce.dorado"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity"
                title={`Posted by ${announcement.authorName || 'Admin'} - View Instagram`}
              >
                <img 
                  src={announcement.authorAvatar} 
                  alt={announcement.authorName || 'Admin'} 
                  className="h-6 w-6 rounded-full object-cover border border-gray-200"
                />
              </a>
            )}
          </div>
          <div>
            <div className="text-sm font-medium text-gray-900">
              {announcement.title}
              {announcement.isPinned && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary">
                  Pinned
                </span>
              )}
              {announcement.language && announcement.language !== 'en' && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                  {announcement.language.toUpperCase()}
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500">
              by {announcement.authorName || 'Admin'}
            </div>
          </div>
        </div>
      </td>
      <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
          {announcement.announcementType}
        </span>
      </td>
      <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
          announcement.priority === 'urgent' ? 'bg-red-100 text-red-800' :
          announcement.priority === 'high' ? 'bg-orange-100 text-orange-800' :
          announcement.priority === 'normal' ? 'bg-blue-100 text-blue-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {announcement.priority}
        </span>
      </td>
      <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          announcement.isPublished ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {announcement.isPublished ? 'Published' : 'Draft'}
        </span>
      </td>
      <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {announcement.publishedAt ? new Date(announcement.publishedAt).toLocaleDateString() : '-'}
      </td>
      <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-medium">
        <div className="flex gap-2">
          <button
            onClick={() => navigate(`/admin/announcements/${announcement.id}`)}
            className="text-blue-600 hover:text-blue-900"
            title="Edit"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => navigate(`/admin/announcements/${announcement.id}/translate`)}
            className="text-purple-600 hover:text-purple-900"
            title="Create Translation"
            disabled
          >
            Translate
          </button>
          <button
            onClick={() => onTogglePublish(announcement.id, announcement.isPublished)}
            className={`${announcement.isPublished ? 'text-gray-600 hover:text-gray-900' : 'text-green-600 hover:text-green-900'}`}
            title={announcement.isPublished ? 'Unpublish' : 'Publish'}
          >
            {announcement.isPublished ? 'Unpublish' : 'Publish'}
          </button>
          <button
            onClick={() => onTogglePin(announcement.id, announcement.isPinned)}
            className="text-primary hover:text-primary-700"
            title={announcement.isPinned ? 'Unpin' : 'Pin'}
          >
            {announcement.isPinned ? 'Unpin' : 'Pin'}
          </button>
          <button
            onClick={() => onDelete(announcement.id)}
            className="text-red-600 hover:text-red-900"
            title="Delete"
          >
            Delete
          </button>
        </div>
      </td>
    </tr>
  )
})

export default AnnouncementTableRow
